import { useMemo } from 'react';
import { Input } from '../../components/forms/Input';
import { PlainTextTemplateEditor } from '../../components/forms/PlainTextTemplateEditor';
import { SingleSelect } from '../../components/forms/SingleSelect';
import { TextArea } from '../../components/forms/TextArea';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import {
	ANTHROPIC_MODEL_OPTIONS,
	OPENAI_MODEL_OPTIONS,
} from '../constants/llmModelOptions';
import { convertDataSourcesToVariables } from '../utils/dataSourceToVariables';
import { AgentToolSelector, type SelectedTool } from './AgentToolSelector';

interface ToolConfig {
	toolId: number;
	type: string;
	config?: Record<string, unknown>;
}

interface EvaluateStepConfigProps {
	config: {
		provider?: 'anthropic' | 'openai';
		model?: string;
		systemPrompt?: string;
		userPrompt?: string;
		tools?: Array<ToolConfig>;
		maxTurns?: number;
		maxTokens?: number;
	};
	setConfig: (config: EvaluateStepConfigProps['config']) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getAvailableDataSources?: () => any[];
	getDisplayText?: (value: unknown) => string;
}

const PROVIDER_OPTIONS = [
	{ value: 'anthropic', label: 'Anthropic Claude' },
	{ value: 'openai', label: 'OpenAI GPT' },
];

// Model lists live in ../constants/llmModelOptions so a second LLM-backed step
// can offer the same choices without a second copy drifting out of sync.
const ANTHROPIC_MODELS = ANTHROPIC_MODEL_OPTIONS;
const OPENAI_MODELS = OPENAI_MODEL_OPTIONS;

export function EvaluateStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
}: EvaluateStepConfigProps) {
	const { t } = useTranslation();
	const colors = useThemeColors();

	// Convert config tools to SelectedTool format for AgentToolSelector
	const selectedTools: Array<SelectedTool> = useMemo(() => {
		if (config.tools == null || config.tools.length === 0) {
			return [];
		}
		return config.tools.map((tool) => ({
			toolId: tool.toolId,
			type: tool.type,
			config: tool.config,
		}));
	}, [config.tools]);

	// Convert available data sources to context-aware variables for the merge variable picker
	const contextAwareVariables = useMemo(() => {
		if (getAvailableDataSources == null) {
			return [];
		}
		const dataSources = getAvailableDataSources();
		return convertDataSourcesToVariables(dataSources);
	}, [getAvailableDataSources]);

	const updateConfig = <K extends keyof EvaluateStepConfigProps['config']>(
		key: K,
		value: EvaluateStepConfigProps['config'][K],
	) => {
		setConfig({ ...config, [key]: value });
	};

	const handleProviderChange = (provider: string) => {
		// When provider changes, reset model to the first option for that provider
		const defaultModel =
			provider === 'anthropic' ? 'claude-sonnet-4-20250514' : 'gpt-4o';
		setConfig({
			...config,
			provider: provider as 'anthropic' | 'openai',
			model: defaultModel,
		});
	};

	// Handle tool selection changes from AgentToolSelector
	const handleToolSelectionChange = (tools: Array<SelectedTool>) => {
		// Convert SelectedTool[] to ToolConfig[] - type is now included in SelectedTool
		const toolConfigs: Array<ToolConfig> = tools.map((st) => ({
			toolId: st.toolId,
			type: st.type ?? String(st.toolId),
			config: st.config,
		}));
		updateConfig('tools', toolConfigs.length > 0 ? toolConfigs : undefined);
	};

	const modelOptions =
		config.provider === 'openai' ? OPENAI_MODELS : ANTHROPIC_MODELS;

	return (
		<div className="space-y-6">
			<div>
				<h3
					className={`text-base font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('AI Processing Configuration')}
				</h3>

				{/* Provider Selection */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('LLM Provider')}
					</label>
					<SingleSelect
						name="evaluate-provider"
						value={config.provider ?? 'anthropic'}
						onChange={handleProviderChange}
						options={PROVIDER_OPTIONS}
					/>
				</div>

				{/* Model Selection */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Model')}
					</label>
					<SingleSelect
						name="evaluate-model"
						value={config.model ?? modelOptions[0]?.value ?? ''}
						onChange={(value) => {
							updateConfig('model', value);
						}}
						options={modelOptions}
					/>
				</div>

				{/* System Prompt */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('System Prompt')}
					</label>
					<TextArea
						value={config.systemPrompt ?? ''}
						onChange={(e) => {
							updateConfig('systemPrompt', e.target.value);
						}}
						placeholder={t(
							'Enter the system prompt that defines the AI behavior and context...',
						)}
						rows={6}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Define the AI assistant personality, role, and any constraints.',
						)}
					</p>
				</div>

				{/* User Prompt with Variable Injection */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('User Prompt')}
					</label>
					<PlainTextTemplateEditor
						value={config.userPrompt ?? ''}
						onChange={(value) => {
							updateConfig('userPrompt', value);
						}}
						placeholder={t(
							'Enter the task or question to process. Use the variable picker to insert dynamic values...',
						)}
						minHeight="120px"
						rows={4}
						contextAwareVariables={contextAwareVariables}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'The task to perform. Use the variable picker to insert values from the trigger or previous steps.',
						)}
					</p>
				</div>

				{/* Tools Selection */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Available Tools')}
					</label>
					<p className={`text-xs mb-2 ${colors.classes.textSecondary}`}>
						{t(
							'Select the tools that the AI can use to perform actions. Some tools may require additional configuration.',
						)}
					</p>
					<div
						className={`border rounded-lg overflow-hidden ${colors.classes.borderDefault}`}
						style={{ height: '400px' }}
					>
						<AgentToolSelector
							selectedTools={selectedTools}
							onSelectionChange={handleToolSelectionChange}
						/>
					</div>
				</div>

				{/* Max Turns */}
				<div className="grid grid-cols-2 gap-4 mb-4">
					<div>
						<label
							className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
						>
							{t('Max Turns')}
						</label>
						<Input
							type="number"
							value={config.maxTurns ?? 20}
							onChange={(e) => {
								const value = parseInt(e.target.value, 10);
								updateConfig('maxTurns', isNaN(value) ? 20 : value);
							}}
							min={1}
							max={100}
						/>
						<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t('Maximum LLM turns before stopping.')}
						</p>
					</div>

					{/* Max Tokens */}
					<div>
						<label
							className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
						>
							{t('Max Output Tokens')}
						</label>
						<Input
							type="number"
							value={config.maxTokens ?? 4096}
							onChange={(e) => {
								const value = parseInt(e.target.value, 10);
								updateConfig('maxTokens', isNaN(value) ? 4096 : value);
							}}
							min={100}
							max={16384}
						/>
						<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t('Maximum tokens per LLM response.')}
						</p>
					</div>
				</div>
			</div>

			{/* Info Box */}
			<div className={`p-3 rounded ${colors.classes.calloutInfo}`}>
				<p className={`text-sm ${colors.classes.textPrimary}`}>
					<strong>{t('Note:')}</strong>{' '}
					{t(
						'The AI will process the user prompt using the specified model and available tools. It can make multiple tool calls across turns until completing the task or reaching the max turns limit.',
					)}
				</p>
			</div>
		</div>
	);
}
